import React, { useCallback, useRef } from "react";
import { Box } from "@mui/material";

const hideVerticalScrollbarSx = {
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none" },
};

/**
 * Vertical piece tray beside the board — fixed column count (row-major), scrolls down.
 * `containerRef` is attached to the scroll element (hit-testing / drag offsets).
 */
export default function PieceTrayScrollColumns({
    children,
    containerRef,
    columnCount = 3,
    cellSizePx,
    maxHeightPx,
    gapPx = 0,
    sx,
}) {
    const scrollRef = useRef(null);
    const cols = Math.max(1, Math.floor(columnCount) || 1);
    const cell = Math.max(1, Math.round(cellSizePx) || 1);
    const gap = Math.max(0, Math.round(gapPx) || 0);
    const widthPx = cols * cell + Math.max(0, cols - 1) * gap;

    const setScrollEl = useCallback(
        (el) => {
            scrollRef.current = el;
            if (!containerRef) return;
            if (typeof containerRef === "function") {
                containerRef(el);
            } else {
                containerRef.current = el;
            }
        },
        [containerRef],
    );

    return (
        <Box
            ref={setScrollEl}
            sx={{
                width: widthPx,
                maxWidth: widthPx,
                height: "100%",
                maxHeight: maxHeightPx ?? "100%",
                flexShrink: 0,
                position: "relative",
                pointerEvents: "auto",
                boxSizing: "border-box",
                touchAction: "pan-y",
                ...hideVerticalScrollbarSx,
                ...sx,
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${cols}, ${cell}px)`,
                    gridAutoFlow: "row",
                    gridAutoRows: `${cell}px`,
                    columnGap: `${gap}px`,
                    rowGap: `${gap}px`,
                    width: widthPx,
                    height: "max-content",
                    minHeight: "100%",
                    alignContent: "start",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}
